import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Trophy, Loader2, Image, Video, Layout, FileText, Palette,
  Target, CheckCircle2, Clock, AlertTriangle,
  TrendingUp, Calendar, ArrowLeft, Users
} from 'lucide-react';
import { SectionHeader } from '@/components/SectionHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRIC_CONFIG = {
  POS: { label: 'Poster', icon: Image, color: 'text-pink-400', bg: 'bg-pink-500/10', bar: 'bg-pink-500' },
  VIA: { label: 'Video (Ads)', icon: Video, color: 'text-purple-400', bg: 'bg-purple-500/10', bar: 'bg-purple-500' },
  VID: { label: 'Video (Organic)', icon: Video, color: 'text-fuchsia-400', bg: 'bg-fuchsia-500/10', bar: 'bg-fuchsia-500' },
};
const METRIC_KEYS = ['POS', 'VIA', 'VID'];

const monthYearString = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const Avatar = ({ name, url, size = 36 }) => {
  const initials = (name || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  if (url) {
    return <img src={url} alt={name} className="rounded-full object-cover border border-[hsl(var(--border))]" style={{ width: size, height: size }} />;
  }
  return (
    <div
      className="rounded-full flex items-center justify-center font-bold text-white bg-gradient-to-br from-pink-500 to-rose-600 border border-[hsl(var(--border))]"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >{initials}</div>
  );
};

export default function CreativePerformancePage() {
  const today = new Date();
  const [month, setMonth] = useState(monthYearString(today));
  const [overview, setOverview] = useState(null);     // team-overview data
  const [perfData, setPerfData] = useState(null);     // ranking data
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const h = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = async (m) => {
    setLoading(true);
    try {
      const [ovRes, pfRes] = await Promise.all([
        axios.get(`${API}/creative-scorecard/team-overview?period=${m}`, h),
        axios.get(`${API}/creative/performance?month=${m}`, h),
      ]);
      setOverview(ovRes.data);
      setPerfData(pfRes.data);
    } catch (e) {
      toast.error('Failed to load creative performance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(month); /* eslint-disable-next-line */ }, [month]);

  const goPrevMonth = () => {
    const [y, m] = month.split('-').map(Number);
    setMonth(monthYearString(new Date(y, m - 2, 1)));
  };
  const goNextMonth = () => {
    const [y, m] = month.split('-').map(Number);
    setMonth(monthYearString(new Date(y, m, 1)));
  };

  const team = overview?.team || {};
  const channels = overview?.channels || [];
  const perfTeam = perfData?.team || {};
  const members = perfData?.members || [];

  const visibleMembers = members.filter((m) => m.orders > 0);

  const [y, m] = month.split('-').map(Number);
  const monthLabel = `${MONTH_NAMES[m - 1]} ${y}`;

  // Helper for ranking by_type: get count of (poster | video_ads + video | video_organic)
  const getCountFor = (byType, key) => {
    if (!byType) return null;
    if (key === 'video_ads') {
      const a = byType.video_ads || { total: 0, completed: 0 };
      const legacy = byType.video || { total: 0, completed: 0 };
      return { total: (a.total || 0) + (legacy.total || 0), completed: (a.completed || 0) + (legacy.completed || 0) };
    }
    return byType[key];
  };

  if (loading && !overview) {
    return (
      <ResponsiveLayout>
        <div className="flex-1 flex items-center justify-center min-h-screen bg-[hsl(var(--background))]">
          <Loader2 className="w-8 h-8 animate-spin text-pink-400" />
        </div>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        <SectionHeader
          icon={Trophy}
          title="Creative Team Performance"
          subtitle="Per-channel scorecard + individual ranking from Creative Requests"
          iconBg="bg-gradient-to-br from-amber-500 to-orange-600 shadow-amber-500/20"
          action={
            <Link to="/team/creative-request">
              <Button variant="outline" data-testid="back-to-creative-requests" className="border-[hsl(var(--border))]">
                <ArrowLeft size={16} className="mr-2" /> Back to Requests
              </Button>
            </Link>
          }
        />

        {/* Month Picker */}
        <div className="flex items-center gap-2 mb-6">
          <Button variant="outline" size="sm" onClick={goPrevMonth} data-testid="cp-prev-month" className="border-[hsl(var(--border))]">‹</Button>
          <div className="flex items-center gap-2 px-4 py-2 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-lg">
            <Calendar size={14} className="text-amber-400" />
            <span className="text-sm font-semibold" data-testid="cp-month-label">{monthLabel}</span>
          </div>
          <Button variant="outline" size="sm" onClick={goNextMonth} data-testid="cp-next-month" className="border-[hsl(var(--border))]">›</Button>
          {loading && <Loader2 size={16} className="animate-spin text-pink-400 ml-2" />}
        </div>

        {/* Team Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <StatCard label="Total Orders"    value={perfTeam.total_orders || 0}  icon={Trophy}        accent="text-amber-400"   testid="cp-stat-orders" />
          <StatCard label="Completed"       value={perfTeam.completed || 0}     icon={CheckCircle2}  accent="text-emerald-400" testid="cp-stat-completed" />
          <StatCard label="In Progress"     value={perfTeam.in_progress || 0}   icon={Clock}         accent="text-blue-400"    testid="cp-stat-inprogress" />
          <StatCard label="Not Started"     value={perfTeam.not_started || 0}   icon={AlertTriangle} accent="text-gray-400"    testid="cp-stat-notstarted" />
          <StatCard label="Overdue"         value={perfTeam.overdue || 0}       icon={AlertTriangle} accent="text-red-400"     testid="cp-stat-overdue" />
        </div>

        {/* Team Target vs Output (driven by scorecard nodes) */}
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5 mb-6" data-testid="cp-team-overview">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-semibold flex items-center gap-2"><Target size={16} className="text-pink-400" /> Team Target vs Output</h3>
              <p className="text-xs text-[hsl(var(--text-muted))] mt-1">Aggregated from per-channel "Creative" scorecard targets (Admin → Scorecard → Targets)</p>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Total</div>
                <div className="text-lg font-bold"><span className="text-emerald-400" data-testid="cp-team-actual">{team.total_actual || 0}</span> <span className="text-[hsl(var(--text-muted))]">/</span> <span data-testid="cp-team-target">{team.total_target || 0}</span></div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Completion</div>
                <div className="text-lg font-bold text-emerald-400" data-testid="cp-team-completion-pct">{team.completion_pct || 0}%</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">On-Time</div>
                <div className="text-lg font-bold text-amber-400" data-testid="cp-team-ontime-pct">{team.on_time_pct || 0}%</div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {METRIC_KEYS.map((mk) => {
              const cfg = METRIC_CONFIG[mk];
              const Icon = cfg.icon;
              const tgt = team.targets?.[mk] || 0;
              const actual = team.actuals?.[mk] || 0;
              const pct = tgt > 0 ? Math.min(100, Math.round((actual / tgt) * 100)) : 0;
              return (
                <div key={mk} className="border border-[hsl(var(--border))] rounded-lg p-3" data-testid={`cp-team-metric-${mk}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`p-1.5 rounded ${cfg.bg}`}><Icon size={14} className={cfg.color} /></span>
                      <span className="text-sm font-semibold">{cfg.label}</span>
                    </div>
                    <span className="text-xs text-[hsl(var(--text-muted))]">{actual}/{tgt}</span>
                  </div>
                  <div className="w-full bg-[hsl(var(--surface-elevated))] rounded-full h-1.5 overflow-hidden">
                    <div className={`${cfg.bar} h-full transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[11px] mt-1.5 text-[hsl(var(--text-muted))]">{pct}% completed</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Per-Channel Cards */}
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5 mb-6" data-testid="cp-channels-section">
          <h3 className="text-base font-semibold flex items-center gap-2 mb-4"><Layout size={16} className="text-cyan-400" /> Per-Channel Breakdown</h3>
          {channels.length === 0 ? (
            <p className="text-sm text-[hsl(var(--text-muted))] py-4 text-center">No Creative org nodes found. Set up "Creative" STAFF nodes under each channel via Admin → Scorecard.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {channels.map((c) => {
                const totalTgt = c.total_target || 0;
                const pct = totalTgt > 0 ? Math.min(100, Math.round((c.total_actual / totalTgt) * 100)) : 0;
                return (
                  <div key={c.node_id} className="border border-[hsl(var(--border))] rounded-lg p-3 bg-[hsl(var(--surface-elevated))]/30" data-testid={`cp-channel-${c.node_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-sm truncate">{c.channel_name}</div>
                      {c.is_saas && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded">SaaS</span>}
                    </div>
                    <div className="space-y-1.5 text-xs">
                      {METRIC_KEYS.map((mk) => {
                        const cfg = METRIC_CONFIG[mk];
                        const tgt = c.targets?.[mk] || 0;
                        const act = c.actuals?.[mk] || 0;
                        const mpct = tgt > 0 ? Math.min(100, Math.round((act / tgt) * 100)) : 0;
                        return (
                          <div key={mk} className="flex items-center gap-2">
                            <span className={`text-[10px] font-bold w-12 ${cfg.color}`}>{cfg.label.replace(' (Ads)', ' Ads').replace(' (Organic)', ' Org')}</span>
                            <div className="flex-1 bg-[hsl(var(--surface))] rounded-full h-1.5 overflow-hidden">
                              <div className={`${cfg.bar} h-full`} style={{ width: `${mpct}%` }} />
                            </div>
                            <span className="text-[10px] tabular-nums whitespace-nowrap"><span className={cfg.color + ' font-bold'}>{act}</span>/{tgt}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="border-t border-[hsl(var(--border))] mt-2.5 pt-2 flex items-center justify-between text-[11px]">
                      <span className="text-[hsl(var(--text-muted))]">Completion</span>
                      <span className={`font-bold ${pct >= 80 ? 'text-emerald-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Individual Ranking */}
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl overflow-hidden" data-testid="cp-leaderboard">
          <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
            <h3 className="text-base font-semibold flex items-center gap-2"><Users size={16} className="text-amber-400" /> Individual Ranking</h3>
            <span className="text-xs text-[hsl(var(--text-muted))]">Ranked by Completed → Orders → On-Time %</span>
          </div>
          {visibleMembers.length === 0 ? (
            <div className="p-8 text-center text-[hsl(var(--text-muted))] text-sm">No orders attributed to any team member this month.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-[hsl(var(--surface-elevated))] text-xs uppercase tracking-wide text-[hsl(var(--text-muted))]">
                  <tr>
                    <th className="text-left px-4 py-3">Rank</th>
                    <th className="text-left px-4 py-3">Member</th>
                    <th className="text-center px-3 py-3">Orders</th>
                    <th className="text-center px-3 py-3">Poster</th>
                    <th className="text-center px-3 py-3">Video Ads</th>
                    <th className="text-center px-3 py-3">Video Organic</th>
                    <th className="text-center px-3 py-3">Banner</th>
                    <th className="text-center px-3 py-3">Social</th>
                    <th className="text-center px-3 py-3">Other</th>
                    <th className="text-center px-3 py-3">Done</th>
                    <th className="text-center px-3 py-3">On-Time</th>
                    <th className="text-center px-3 py-3">Avg Days</th>
                    <th className="text-center px-3 py-3">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleMembers.map((mem, idx) => {
                    const rankBadge = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`;
                    return (
                      <tr key={mem.user_id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-elevated))]/50" data-testid={`cp-row-${mem.user_id}`}>
                        <td className="px-4 py-3 text-sm font-bold">{rankBadge}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <Avatar name={mem.name} url={mem.avatar_url} size={36} />
                            <div className="text-sm font-semibold">{mem.name}</div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center text-sm font-semibold">{mem.orders}</td>
                        <td className="px-3 py-3 text-center text-sm"><TypeCell stats={getCountFor(mem.by_type, 'poster')} color="text-pink-400" /></td>
                        <td className="px-3 py-3 text-center text-sm"><TypeCell stats={getCountFor(mem.by_type, 'video_ads')} color="text-purple-400" /></td>
                        <td className="px-3 py-3 text-center text-sm"><TypeCell stats={getCountFor(mem.by_type, 'video_organic')} color="text-fuchsia-400" /></td>
                        <td className="px-3 py-3 text-center text-sm"><TypeCell stats={getCountFor(mem.by_type, 'banner')} color="text-blue-400" /></td>
                        <td className="px-3 py-3 text-center text-sm"><TypeCell stats={getCountFor(mem.by_type, 'social_media')} color="text-cyan-400" /></td>
                        <td className="px-3 py-3 text-center text-sm"><TypeCell stats={getCountFor(mem.by_type, 'other')} color="text-gray-400" /></td>
                        <td className="px-3 py-3 text-center text-sm font-bold text-emerald-400">{mem.completed}</td>
                        <td className="px-3 py-3 text-center">
                          <Badge className={`${mem.on_time_pct >= 80 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : mem.on_time_pct >= 50 ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'} border text-xs`}>{mem.on_time_pct}%</Badge>
                        </td>
                        <td className="px-3 py-3 text-center text-sm text-[hsl(var(--text-secondary))]">{mem.avg_turnaround_days != null ? `${mem.avg_turnaround_days}d` : '—'}</td>
                        <td className="px-3 py-3 text-center text-sm font-semibold text-amber-400">{mem.contribution_pct}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-[hsl(var(--text-muted))] mt-4 text-center">
          Tip: Targets are managed in <strong>Admin → Scorecard → Targets</strong> (per-channel "Creative" node).
          On-Time % counts deliveries completed on or before due date.
        </p>
      </div>
    </ResponsiveLayout>
  );
}

const StatCard = ({ label, value, icon: Icon, accent, testid }) => (
  <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-4 flex items-center gap-3" data-testid={testid}>
    <div className="p-2 rounded-lg bg-[hsl(var(--surface-elevated))]">
      <Icon size={20} className={accent} />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">{label}</div>
      <div className={`text-2xl font-bold ${accent}`}>{value}</div>
    </div>
  </div>
);

const TypeCell = ({ stats, color }) => {
  if (!stats || stats.total === 0) return <span className="text-[hsl(var(--text-muted))]">—</span>;
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`font-bold ${color}`}>{stats.completed}</span>
      <span className="text-[hsl(var(--text-muted))]">/{stats.total}</span>
    </span>
  );
};
